import { useEffect, useState, useMemo } from 'react';
import {
    Box,
    UnorderedList,
    ListItem,
    Flex,
    useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import {
    Button,
    Text,
    Alert,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ConfirmationDialog,
} from 'components/v4';
import TransferringTables from 'components/tables/TransferringTables/TransferringTables';
import useAcknowledgePlatformNumbers from '../../queries/useAcknowledgePlatformNumbers';

const ConfirmNumbersModal = ({
    isOpen = false,
    onClose = () => {},
    onConfirm = () => {},
    initialNumbers = [],
    refetch,
}) => {
    const safeInitialNumbers = useMemo(
        () => (Array.isArray(initialNumbers) ? initialNumbers : []),
        [initialNumbers],
    );
    const { currentScope } = useSelector((state) => ({ ...state.login }));
    const { currentWholesaler: wholesalerID, wholesalers } = useSelector(
        (state) => state.navigationLists,
    );

    const wholesalerName = useMemo(() => {
        return (
            wholesalers?.find(({ id }) => id === wholesalerID)?.name ||
            'Your Telecom Provider'
        );
    }, [wholesalers, wholesalerID]);

    const [confirmationRequired, setConfirmationRequired] =
        useState(safeInitialNumbers);
    const [confirmed, setConfirmed] = useState([]);

    // Confirmation dialog state
    const {
        isOpen: isConfirmDialogOpen,
        onOpen: onConfirmDialogOpen,
        onClose: onConfirmDialogClose,
    } = useDisclosure();

    useEffect(() => {
        if (isOpen && safeInitialNumbers.length > 0) {
            setConfirmationRequired(safeInitialNumbers);
            setConfirmed([]);
        }
    }, [isOpen, safeInitialNumbers]);

    const { mutate: acknowledgeNumbers, isPending: isAcknowledging } =
        useAcknowledgePlatformNumbers(confirmed, currentScope, refetch);

    const numberColDefs = [
        {
            isCheckbox: true,
            label: 'Select',
            dataKey: 'checkbox',
        },
        {
            label: 'Number',
            dataKey: 'telephoneNumber',
            minWidth: 200,
            flexGrow: 1,
            maxWidth: 300,
            cellRenderer: ({ rowData }) => {
                const number = rowData?.telephoneNumber;
                return number ? `+${number}` : '';
            },
        },
    ];

    const confirmationRequiredConfig = {
        captionName: 'Confirmation Required ',
        placeholder: 'Search numbers...',
        colDefs: numberColDefs,
        searchField: 'telephoneNumber',
    };

    const confirmedConfig = {
        captionName: 'Confirmed ',
        placeholder: 'Search numbers...',
        colDefs: numberColDefs,
        searchField: 'telephoneNumber',
    };

    const walkthroughIds = {
        moveToChosen: '/confirm-transfer/button/move/confirmed',
        moveToAvailable: '/confirm-transfer/button/move/required',
        moveAllToChosen: '/confirm-transfer/button/move/confirmed/all',
        moveAllToAvailable: '/confirm-transfer/button/move/required/all',
    };

    const handleSaveClick = () => {
        if (confirmed.length > 0) {
            onConfirmDialogOpen();
        }
    };

    const handleConfirmSave = () => {
        acknowledgeNumbers();
        onConfirm?.(confirmed);
    };

    const handleCancel = () => {
        setConfirmationRequired(safeInitialNumbers);
        setConfirmed([]);
        onClose?.();
    };

    if (!isOpen) return null;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size="6xl"
                closeOnOverlayClick>
                <ModalOverlay />
                <ModalContent sx={{ marginTop: '30px', marginBottom: '30px' }}>
                    <ModalHeader fontSize="2xl" fontWeight="bold">
                        Confirm Transfer
                    </ModalHeader>

                    <ModalBody>
                        <Alert status="info" mb={6} borderRadius="md">
                            <Box>
                                <Text fontSize="md" mb={2}>
                                    The following numbers have been detected as
                                    existing in the Teams Tenant and require
                                    confirmation to transfer.
                                </Text>
                                <UnorderedList spacing={1} ml={4}>
                                    <ListItem>
                                        All numbers shall be uploaded to
                                        Operator Connect under the{' '}
                                        <Text
                                            as="span"
                                            fontWeight="bold"
                                            color="blue.500">
                                            {wholesalerName}
                                        </Text>{' '}
                                        profile.
                                    </ListItem>
                                    <ListItem>
                                        All numbers assigned to users shall
                                        remain assigned.
                                    </ListItem>
                                    <ListItem>
                                        All calls from these numbers shall be
                                        routed to{' '}
                                        <Text as="span" fontWeight="bold">
                                            {wholesalerName}
                                        </Text>
                                    </ListItem>
                                    <ListItem>
                                        If a number was originally assigned to a
                                        Direct Routing user, to ensure outbound
                                        calls work, Voice Routing Policy must be
                                        removed from the user. To do this, set
                                        the user to <b>Managed by TCAP</b> and
                                        run sync <b>OR</b> have customer update
                                        user manually in Teams Admin
                                    </ListItem>
                                </UnorderedList>
                            </Box>
                        </Alert>
                        <TransferringTables
                            availableData={confirmationRequired}
                            chosenData={confirmed}
                            availableTableConfig={confirmationRequiredConfig}
                            chosenTableConfig={confirmedConfig}
                            isLoading={false}
                            isDisabled={false}
                            onAvailableDataChange={setConfirmationRequired}
                            onChosenDataChange={setConfirmed}
                            walkthroughIds={walkthroughIds}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Flex gap={4}>
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                data-walkthroughid="/confirm-transfer/button/cancel">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveClick}
                                isDisabled={confirmed.length < 1}
                                data-walkthroughid="/confirm-transfer/button/save">
                                Save
                            </Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <ConfirmationDialog
                isOpen={isConfirmDialogOpen}
                onClose={onConfirmDialogClose}
                onConfirm={handleConfirmSave}
                title="Confirm Transfer"
                message={`You are about to confirm the transfer of ${confirmed.length} number(s). Do you want to proceed?`}
                confirmText="Confirm"
                cancelText="Cancel"
                isLoading={isAcknowledging}
            />
        </>
    );
};

export default ConfirmNumbersModal;
